import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, ArrowRight, Search } from 'lucide-react';
import { countries } from '../data/mockData';
import { getAllBlogPosts } from '../services/contentService';
import { useLanguage } from '../context/LanguageContext';
import './BlogSection.css';

function BlogSection() {
  const { language } = useLanguage();
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBlogPosts();
  }, []);  // Empty dependency array - load once on mount

  const loadBlogPosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllBlogPosts();
      if (response.success && response.data) {
        // Transform country_id to countryId for compatibility
        const posts = response.data.map(post => ({
          ...post,
          countryId: post.country_id,
          readTime: post.read_time
        }));
        setBlogPosts(posts);
      }
    } catch (error) {
      console.error('Error loading blog posts:', error);
    } finally {
      setLoading(false);
    }
  }, []);  // No dependencies needed

  const filteredPosts = blogPosts.filter(post => {
    const matchesCountry = selectedCountry === 'all' || post.countryId === selectedCountry;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCountry && matchesSearch;
  });

  // Get unique countries that have blog posts
  const countriesWithBlogs = [...new Set(blogPosts.map(post => post.countryId))];
  const countryOptions = countries.filter(c => countriesWithBlogs.includes(c.id));

  return (
    <section id="blog" className="section blog-section">
      <div className="blog-header">
        <div>
          <h2 className="section-title">
            <BookOpen size={32} />
            Travel Blog
          </h2>
          <p className="section-subtitle">
            Reiseberichte, Tipps & Erfahrungen aus erster Hand
          </p>
        </div>
      </div>

      <div className="blog-filters">
        <div className="search-box">
          <Search size={20} />
          <input 
            type="text"
            placeholder="Blog durchsuchen..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select 
          className="country-filter"
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
        >
          <option value="all">Alle Länder</option>
          {countryOptions.map(country => (
            <option key={country.id} value={country.id}>{country.name}</option>
          ))}
        </select>
      </div>

      <div className="blog-grid">
        {loading ? (
          <div className="loading-state">
            <p>Lade Blogbeiträge...</p>
          </div>
        ) : filteredPosts.length > 0 ? (
          filteredPosts.map(post => {
            const country = countries.find(c => c.id === post.countryId);
            return (
              <Link to={`/blog/${post.id}`} key={post.id} className="blog-card card card-hover" style={{ textDecoration: 'none' }}>
                <div className="blog-image-wrapper">
                  <img src={post.image} alt={post.title} className="blog-image" />
                  <div className="blog-country-badge">{country?.name}</div>
                </div>
                <div className="blog-content">
                  <div className="blog-meta">
                    <span className="blog-date">{new Date(post.date).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    <span className="blog-read-time">
                      <Clock size={14} />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="blog-title">{post.title}</h3>
                  <p className="blog-excerpt">{post.excerpt}</p>
                  <span className="blog-read-more">
                    Weiterlesen
                    <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            );
          })
        ) : (
          <div className="no-results">
            <BookOpen size={48} />
            <p>Keine Blogbeiträge gefunden</p>
            <button className="btn-outline" onClick={() => { setSearchTerm(''); setSelectedCountry('all'); }}>
              Filter zurücksetzen
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default BlogSection;
